import { uid } from 'uid'

// Event name mapping from RudderStack to GA4
const eventNameMapping = {
  // Product events
  'Product Viewed': 'view_item',
  'Product Added': 'add_to_cart',
  'Product Removed': 'remove_from_cart',
  'Product Customised': 'customize_product',
  'Product Design Saved': 'save_product_design',
  'Product Hero Design Selected': 'select_hero_design',
  'Product Saved Design Selected': 'select_saved_design',
  'Product Upgraded': 'upgrade_product',
  'Product Downgraded': 'downgrade_product',
  'Product Lens Tech Selected': 'select_lens_tech',
  'Product Frame Type Selected': 'select_frame_type',

  // Cart events
  'Cart Viewed': 'view_cart',

  // Navigation events
  'Navigation Link Clicked': 'click_nav_link',
  'Menu Product Clicked': 'click_menu_product',
  'Header Logo Clicked': 'click_logo',
  'Header Cart Link Clicked': 'click_cart',

  // Content events
  'Homepage Hero CTA Clicked': 'click_hero_cta',
  'Homepage Pagination Clicked': 'click_pagination',

  // Form events
  'Form Submitted Success': 'form_submit',
  'Form Submitted Invalid': 'form_submit_failed',

  // Email signup events
  'Signup Modal Seen': 'view_signup_modal',
  'Signup Modal Closed': 'close_signup_modal',
  'Completed SunGod+ Signup': 'signup',
  'Content Preferences Given': 'set_preferences',

  // Support events
  'Support Article Viewed': 'view_support_article',
  'Support Articles Searched': 'search_support',
  'Support Category Viewed': 'view_support_category',

  // Lens guide events
  'Lens Guide Opened': 'view_lens_guide',
  'Lens Guide Closed': 'close_lens_guide',

  // Experiment events
  'Experiment Viewed': 'view_experiment',
}

// Flatten nested objects for GA4 (GA4 doesn't support nested properties)
function flattenProperties(obj, prefix = '') {
  const flattened = {}

  for (const key in obj) {
    if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
      // Recursively flatten nested objects
      const nested = flattenProperties(obj[key], `${prefix}${key}_`)
      Object.assign(flattened, nested)
    } else {
      // Handle arrays and primitives
      const flatKey = `${prefix}${key}`
      flattened[flatKey] = obj[key]
    }
  }

  return flattened
}

// Transform properties to GA4 format
function transformProperties(properties = {}) {
  const transformed = flattenProperties(properties)

  // Ensure required GA4 properties exist
  if (properties.items || properties.products) {
    transformed.items = properties.items || properties.products
  }

  if (properties.currency) {
    transformed.currency = properties.currency
  }

  if (properties.value || properties.revenue) {
    transformed.value = properties.value || properties.revenue
  }

  if (properties.order_id || properties.cart_id) {
    transformed.transaction_id = properties.order_id || properties.cart_id
  }

  // Add event_id for deduplication
  transformed.event_id = uid(32)

  return transformed
}

export default {
  track(eventName, properties = {}) {
    if (!window.gtag) return

    try {
      // Get user store
      const userStore = useUserStore()

      // Map RudderStack event names to GA4 equivalents
      const ga4EventName = eventNameMapping[eventName] || eventName.toLowerCase().replace(/\s+/g, '_')

      // Transform properties for GA4
      const transformedProperties = transformProperties(properties)

      // Set user properties if available
      if (userStore?.email) {
        window.gtag('set', 'user_properties', {
          user_email: userStore.email,
          is_sungod_plus_member: !!userStore.email,
        })
      }

      if (userStore?.uuid) {
        window.gtag('config', window.GA_MEASUREMENT_ID || 'G-T0S5H12K5S', {
          user_id: userStore.uuid,
        })
      }

      // Send the event
      window.gtag('event', ga4EventName, transformedProperties)
    } catch (error) {
      console.error('[GA4] Event tracking failed:', error)
    }
  },

  page(pageName = null, properties = {}) {
    if (!window.gtag) return

    try {
      const pageProperties = transformProperties(properties)

      // Add page-specific properties
      if (pageName) {
        pageProperties.page_title = pageName
      }

      window.gtag('event', 'page_view', pageProperties)
    } catch (error) {
      console.error('[GA4] Page tracking failed:', error)
    }
  },

  identify(userId = null, traits = {}) {
    if (!window.gtag) return

    try {
      // Set user ID if provided
      if (userId) {
        window.gtag('config', window.GA_MEASUREMENT_ID || 'G-T0S5H12K5S', {
          user_id: userId,
        })
      }

      // Set user properties from traits
      if (Object.keys(traits).length > 0) {
        const userProperties = flattenProperties(traits)
        window.gtag('set', 'user_properties', userProperties)
      }
    } catch (error) {
      console.error('[GA4] User identification failed:', error)
    }
  },

  // Ecommerce specific events following GA4 Enhanced Ecommerce
  viewItem(product) {
    if (!window.gtag) return

    try {
      window.gtag('event', 'view_item', {
        event_id: uid(32),
        currency: product.currency || 'GBP',
        value: product.value || product.price,
        items: [
          {
            item_id: product.sku || product.id,
            item_name: product.name,
            item_category: product.category,
            price: product.value || product.price,
            quantity: 1,
          },
        ],
      })
    } catch (error) {
      console.error('[GA4] View item tracking failed:', error)
    }
  },

  addToCart(product) {
    if (!window.gtag) return

    try {
      window.gtag('event', 'add_to_cart', {
        event_id: uid(32),
        currency: product.currency || 'GBP',
        value: product.value || product.price,
        items: [
          {
            item_id: product.sku || product.id,
            item_name: product.name,
            item_category: product.category,
            price: product.value || product.price,
            quantity: product.quantity || 1,
          },
        ],
      })
    } catch (error) {
      console.error('[GA4] Add to cart tracking failed:', error)
    }
  },

  removeFromCart(product) {
    if (!window.gtag) return

    try {
      window.gtag('event', 'remove_from_cart', {
        event_id: uid(32),
        currency: product.currency || 'GBP',
        value: product.value || product.price,
        items: [
          {
            item_id: product.sku || product.id,
            item_name: product.name,
            item_category: product.category,
            price: product.value || product.price,
            quantity: product.quantity || 1,
          },
        ],
      })
    } catch (error) {
      console.error('[GA4] Remove from cart tracking failed:', error)
    }
  },

  viewCart(cartData) {
    if (!window.gtag) return

    try {
      window.gtag('event', 'view_cart', {
        event_id: uid(32),
        currency: cartData.currency || 'GBP',
        value: cartData.value || cartData.total,
        items: cartData.items || [],
      })
    } catch (error) {
      console.error('[GA4] View cart tracking failed:', error)
    }
  },

  // Custom dimensions and metrics
  setCustomDimensions(dimensions = {}) {
    if (!window.gtag) return

    try {
      window.gtag('config', window.GA_MEASUREMENT_ID || 'G-T0S5H12K5S', {
        custom_map: dimensions,
      })
    } catch (error) {
      console.error('[GA4] Custom dimensions failed:', error)
    }
  },
}
